// 路由配置
//
// 5 个主路由 + 登录 + 详情 + 进入游戏。
// 大多数页面登录无关；只有 Library/Mine/Play 走 requireAuth 钩子。
// meta.titleKey 是 i18n key，beforeEach 里解析成 <标题> - <appName> 设给 document.title。

import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/stores/user";
import { i18n } from "@/i18n";
import { edition } from "@edition";
import { devicePage } from "./devicePage";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/home",
  },
  {
    path: "/home",
    name: "Home",
    component: edition.review.enabled
      ? () => import("@/pages/review/CloudPcHome.vue")
      : devicePage(
          () => import("@/pages/Home.vue"),
          () => import("@/pages/desktop/Home.vue"),
          "Home",
        ),
    meta: { titleKey: "titles.home" },
  },
  {
    path: "/search",
    name: "Search",
    component: () => import("@/pages/Search.vue"),
    meta: { titleKey: "titles.search" },
  },
  {
    path: "/detail/:id",
    name: "Detail",
    component: devicePage(
      () => import("@/pages/Detail.vue"),
      () => import("@/pages/desktop/Detail.vue"),
      "Detail",
    ),
    meta: { titleKey: "titles.detail" },
    props: true,
  },
  {
    path: "/library",
    name: "Library",
    component: devicePage(
      () => import("@/pages/Library.vue"),
      () => import("@/pages/desktop/Library.vue"),
      "Library",
    ),
    meta: { titleKey: "titles.library" },
  },
  {
    // 首页"更多"进来的分类全量列表（/category/:type/:id?title=）
    path: "/category/:type/:id",
    name: "CategoryList",
    component: devicePage(
      () => import("@/pages/CategoryList.vue"),
      () => import("@/pages/desktop/CategoryList.vue"),
      "CategoryList",
    ),
    meta: { titleKey: "titles.category" },
  },
  {
    path: "/mine",
    name: "Mine",
    component: devicePage(
      () => import("@/pages/Mine.vue"),
      () => import("@/pages/desktop/Mine.vue"),
      "Mine",
    ),
    meta: { titleKey: "titles.mine" },
  },
  {
    // 我的收藏 + 最近玩过 - 从 Mine 页 cell 进入
    path: "/my/games",
    name: "MyGames",
    component: () => import("@/pages/MyGames.vue"),
    meta: { titleKey: "titles.myGames" },
  },
  {
    path: "/detail/:gameId/reviews",
    name: "Reviews",
    component: () => import("@/pages/Reviews.vue"),
    meta: { titleKey: "titles.reviews" },
    props: true,
  },
  {
    path: "/detail/:gameId/videos",
    name: "GameVideos",
    component: () => import("@/pages/GameVideos.vue"),
    meta: { titleKey: "titles.gameVideos" },
    props: true,
  },
  {
    path: "/detail/:gameId/reviews/:commentId",
    name: "CommentReplies",
    component: () => import("@/pages/CommentReplies.vue"),
    meta: { titleKey: "titles.commentReplies" },
    props: true,
  },
  {
    path: "/detail/:gameId/saves",
    name: "Saves",
    component: () => import("@/pages/Saves.vue"),
    meta: { titleKey: "titles.saves", requireAuth: true },
    props: true,
  },
  {
    path: "/subscription",
    name: "Subscription",
    component: devicePage(
      () => import("@/pages/Subscription.vue"),
      () => import("@/pages/desktop/Subscription.vue"),
      "Subscription",
    ),
    meta: { titleKey: "titles.subscription" },
  },
  {
    path: "/sign-in",
    name: "SignIn",
    component: () => import("@/pages/SignIn.vue"),
    meta: { titleKey: "titles.signIn" },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/pages/Settings.vue"),
    meta: { titleKey: "titles.settings" },
  },
  {
    path: "/legal",
    name: "LegalCenter",
    component: () => import("@/pages/LegalCenter.vue"),
    meta: { titleKey: "common.userAgreement" },
  },
  {
    path: "/settings/control",
    name: "ControlSettings",
    component: () => import("@/pages/ControlSettings.vue"),
    meta: { titleKey: "titles.controlSettings" },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/pages/Login.vue"),
    meta: { titleKey: "titles.login", fullscreen: true },
  },
  {
    path: "/play/:gameId",
    name: "Play",
    component: () => import("@/pages/Play.vue"),
    meta: { titleKey: "titles.play", requireAuth: true, fullscreen: true },
    props: true,
  },
  {
    // 兜底 404 → 首页
    path: "/:pathMatch(.*)*",
    redirect: "/home",
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  },
});

router.beforeEach((to) => {
  const meta = to.meta as { requireAuth?: boolean; titleKey?: string };
  // 审核版没有游戏目录：旧链接、历史记录或收藏入口统一回云电脑首页。
  if (
    edition.review.enabled &&
    [
      "Search",
      "Detail",
      "Library",
      "CategoryList",
      "MyGames",
      "Reviews",
      "GameVideos",
      "CommentReplies",
      "Saves",
    ].includes(String(to.name))
  ) {
    return { name: "Home" };
  }
  if (meta.titleKey) {
    const t = i18n.global.t;
    document.title = `${t(meta.titleKey)} - ${t(edition.ui.brandKey)}`;
  }
  if (meta.requireAuth) {
    const user = useUserStore();
    if (!user.isLoggedIn) {
      return { name: "Login", query: { redirect: to.fullPath } };
    }
  }
  return true;
});
